import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Cookie, X, Settings } from "lucide-react";

const BannerContainer = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--surface-color);
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -4px 20px var(--shadow-color);
  z-index: 1000;
  padding: 1rem;
  transform: translateY(${(props) => (props.$isVisible ? "0" : "100%")});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 50vh;
  overflow-y: auto;

  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const CookieIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const TextContent = styled.div`
  flex: 1;
  color: var(--text-color);
  line-height: 1.5;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    font-size: 0.9rem;
    color: var(--text-secondary-color);
    margin-bottom: 0.5rem;
  }

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: stretch;
  }
`;

const Button = styled.button<{
  $variant?: "primary" | "secondary" | "tertiary";
}>`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  white-space: nowrap;

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    padding: 0.875rem 1rem;
  }

  ${(props) => {
    switch (props.$variant) {
      case "primary":
        return `
          background: var(--primary-color);
          color: white;
          
          &:hover {
            background: var(--primary-color);
            opacity: 0.9;
          }
        `;
      case "secondary":
        return `
          background: transparent;
          color: var(--primary-color);
          border: 2px solid var(--primary-color);
          
          &:hover {
            background: var(--primary-color);
            color: white;
          }
        `;
      case "tertiary":
        return `
          background: transparent;
          color: var(--text-secondary-color);
          border: 1px solid var(--border-color);
          
          &:hover {
            background: var(--background-color);
            color: var(--text-color);
          }
        `;
      default:
        return `
          background: var(--primary-color);
          color: white;
        `;
    }
  }}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: var(--background-color);
    color: var(--text-color);
  }

  @media (max-width: 768px) {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
`;

const SettingsModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px var(--shadow-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CookieType = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--background-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;

const CookieTypeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CookieTypeTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
`;

const ToggleSwitch = styled.label<{ $enabled: boolean }>`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${(props) =>
      props.$enabled ? "var(--primary-color)" : "#ccc"};
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      transform: ${(props) =>
        props.$enabled ? "translateX(20px)" : "translateX(0)"};
    }
  }
`;

const CookieTypeDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary-color);
  line-height: 1.4;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
}

const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: true,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
    };
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
    };
    localStorage.setItem("cookieConsent", JSON.stringify(onlyNecessary));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type: keyof CookiePreferences) => {
    if (type === "necessary") return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      <BannerContainer $isVisible={isVisible}>
        <BannerContent>
          <CookieIcon>
            <Cookie size={20} />
          </CookieIcon>
          <TextContent>
            <h3>We use cookies</h3>
            <p>
              We use cookies to enhance your experience and analyze site usage.{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                Learn more
              </a>
            </p>
          </TextContent>
          <ButtonGroup>
            <Button $variant="secondary" onClick={handleRejectAll}>
              Reject
            </Button>
            <Button $variant="primary" onClick={handleAcceptAll}>
              Accept
            </Button>
            <Button $variant="tertiary" onClick={() => setShowSettings(true)}>
              <Settings size={16} />
            </Button>
          </ButtonGroup>
          <CloseButton onClick={handleRejectAll}>
            <X size={20} />
          </CloseButton>
        </BannerContent>
      </BannerContainer>

      <SettingsModal $isOpen={showSettings}>
        <ModalContent>
          <ModalTitle>
            <Settings size={24} />
            Cookie Preferences
          </ModalTitle>

          <CookieType>
            <CookieTypeHeader>
              <CookieTypeTitle>Necessary Cookies</CookieTypeTitle>
              <ToggleSwitch $enabled={true}>
                <input type="checkbox" checked disabled />
                <span className="slider"></span>
              </ToggleSwitch>
            </CookieTypeHeader>
            <CookieTypeDescription>
              These cookies are essential for the website to function properly.
              They cannot be disabled.
            </CookieTypeDescription>
          </CookieType>

          <CookieType>
            <CookieTypeHeader>
              <CookieTypeTitle>Analytics Cookies</CookieTypeTitle>
              <ToggleSwitch $enabled={preferences.analytics}>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => handlePreferenceChange("analytics")}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </CookieTypeHeader>
            <CookieTypeDescription>
              These cookies help us understand how visitors interact with our
              website by collecting and reporting information anonymously.
            </CookieTypeDescription>
          </CookieType>

          <ModalButtons>
            <Button $variant="tertiary" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button $variant="primary" onClick={handleSavePreferences}>
              Save Preferences
            </Button>
          </ModalButtons>
        </ModalContent>
      </SettingsModal>
    </>
  );
};

export default CookieConsentBanner;
